/**
 * Client service for landmark discovery and walk-graph baking.
 *
 * Companion to `streetViewPlacesService.ts`, which wraps the older
 * autocomplete/place-details pair. This one covers the curated catalogue, the
 * Places API (New) search behind it, and the bake that turns a single panorama
 * into an area students can walk.
 */

import { getApiBaseUrl } from '../utils/apiConfig';
import { auth } from '../config/firebase';

async function authHeaders(): Promise<Record<string, string>> {
  const token = await auth.currentUser?.getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const base = () => getApiBaseUrl().replace(/\/$/, '');

export interface CatalogueLandmark {
  id: string;
  name: string;
  subject: string;
  category: string;
  country?: string;
  blurb?: string;
  address?: string;
  lat: number;
  lng: number;
  panoId?: string;
  linkCount?: number;
  walkable?: boolean;
  attribution?: string;
  imageryDate?: string;
}

export interface LandmarkSearchResult {
  placeId: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  rating?: number;
  userRatingCount?: number;
  coverage: { available: boolean; panoId?: string; lat?: number; lng?: number; date?: string; copyright?: string };
}

export interface StreetViewCoverageResult {
  available: boolean;
  panoId?: string;
  lat?: number;
  lng?: number;
  date?: string;
  copyright?: string;
  linkCount?: number;
  walkable?: boolean;
}

export interface WalkGraphNode {
  panoId: string;
  lat: number;
  lng: number;
  heading: number;
  skyboxUrl: string;
  links: { panoId: string; heading: number }[];
  copyright?: string;
  date?: string;
}

export interface WalkGraph {
  graphId: string;
  rootPanoId: string;
  radiusMeters: number;
  zoom: number;
  nodes: Record<string, WalkGraphNode>;
  nodeCount: number;
  truncated: boolean;
  complete: boolean;
}

export interface BakeProgress {
  graphId: string;
  done: boolean;
  nodeCount: number;
  pendingCount: number;
  truncated: boolean;
  bakedThisCall: number;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${base()}${path}`, { headers: await authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || data.message || `Request failed (${res.status})`);
  }
  return data as T;
}

/** The curated catalogue, optionally narrowed to one subject. */
export async function fetchLandmarkCatalogue(
  subject?: string
): Promise<{ landmarks: CatalogueLandmark[]; subjects: string[] }> {
  const q = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  const data = await getJson<{ landmarks: CatalogueLandmark[]; subjects: string[] }>(`/streetview/landmarks${q}`);
  return { landmarks: data.landmarks || [], subjects: data.subjects || [] };
}

/** Free-text search. Results are Street View verified unless `includeUncovered`. */
export async function searchLandmarks(
  query: string,
  opts: { limit?: number; includeUncovered?: boolean } = {}
): Promise<LandmarkSearchResult[]> {
  const params = new URLSearchParams({ q: query });
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.includeUncovered) params.set('verifiedOnly', 'false');
  const data = await getJson<{ results: LandmarkSearchResult[] }>(`/streetview/landmarks/search?${params}`);
  return data.results || [];
}

/** Metadata-only coverage probe used before the expensive panorama load. */
export async function checkStreetViewCoverage(
  lat: number,
  lng: number,
  radius = 50
): Promise<StreetViewCoverageResult> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    throw new Error('Choose a valid location on the globe.');
  }
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng), radius: String(radius) });
  return getJson<StreetViewCoverageResult>(`/streetview/coverage?${params}`);
}

/** A baked graph, or null when nothing has been baked for this id yet. */
export async function fetchWalkGraph(graphId: string): Promise<WalkGraph | null> {
  try {
    return await getJson<WalkGraph>(`/streetview/walk-graph/${encodeURIComponent(graphId)}`);
  } catch (err) {
    if ((err as Error).message.includes('not found')) return null;
    throw err;
  }
}

/**
 * Bakes the walkable area around a panorama, reporting progress as it goes.
 *
 * The server works to a time budget and returns what it managed, so this loops
 * until the graph is complete. `onProgress` fires after every batch, which is
 * what drives the teacher's progress bar — baking forty panoramas takes a
 * couple of minutes and silence would look like a hang.
 */
export async function bakeWalkGraph(
  params: { panoId: string; radiusMeters?: number; zoom?: 2 | 3 | 4; maxNodes?: number },
  onProgress?: (p: BakeProgress) => void
): Promise<BakeProgress> {
  let last: BakeProgress | null = null;

  // Bounded rather than while(true): a server that stopped making progress
  // would otherwise spin here indefinitely.
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const res = await fetch(`${base()}/streetview/walk-graph/bake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify(params),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      throw new Error(data.error || `Bake failed (${res.status})`);
    }

    const progress: BakeProgress = {
      graphId: data.graphId,
      done: !!data.done,
      nodeCount: data.nodeCount || 0,
      pendingCount: data.pendingCount || 0,
      truncated: !!data.truncated,
      bakedThisCall: data.bakedThisCall || 0,
    };
    onProgress?.(progress);
    last = progress;

    if (progress.done) return progress;
    // No forward movement and nothing finished: stop rather than loop on a
    // frontier the server cannot resolve (dead links at the edge of coverage).
    if (progress.bakedThisCall === 0) return progress;
  }

  return last as BakeProgress;
}

/*
  Bake settings are fixed constants rather than per-stop fields.

  The graph id is derived from (panoId, radius, zoom), so as long as the creator
  bakes with these and the player looks up with these, the two agree without
  storing anything new on the stop. That keeps a schema change out of this
  feature; making the radius a teacher-facing control means persisting it, which
  is worth doing only once someone asks for it.

  150m is roughly a city block in every direction — far enough to explore, close
  enough that a class does not scatter.
*/
export const WALK_BAKE_RADIUS_M = 150;
export const WALK_BAKE_ZOOM: 2 | 3 | 4 = 3;

/** Mirrors the server's graph id so a stop can look up its bake without a round trip. */
export const walkGraphId = (rootPanoId: string, radiusMeters: number, zoom: number): string =>
  `${rootPanoId}__r${Math.round(radiusMeters)}__z${zoom}`;
